import { useEffect, useState, useRef } from "react";
// import ButtonComponent from "../../components/ButtonComponent";
import { designDetailsForLead } from "../../redux/features/leadUserCatalogue/leadUserCatalogue";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import AppLoader from "../../components/AppLoader";
import { useLoading } from "../../hooks/useLoading";
import { useTheme } from "../../hooks/useTheme";

// prepare gallery items
import "react-image-gallery/styles/css/image-gallery.css";
import { nanoid } from "nanoid";
import { toastComponent } from "../../components/ToastComponent";
// import backArrow from "../../assets/icons/up arrow.svg";
import { addToCart, getAllCart, removeCartData } from "../../redux/features/leadUserCart/leadUserCart";
import JewelMakerLogo from "../../assets/Logo/brand-logo.svg";
// import ModalComponent from "../../components/ModalComponent";
import CartButton from "../../components/CartButton";

// Import Lucide React icons
import { ChevronRight, ShoppingCart, Minus, Plus, ZoomIn } from "lucide-react";
import ImageHoverZoom from "../../components/ImageHoverZoom";

type Props = {};

// Define types for the data structure (keeping your existing interfaces)
interface DesignWeight {
  id: number;
  design_id: number;
  karat: number;
  net_weight: string;
  gross_weight: string;
  size: number;
}

interface ImageFile {
  id: number;
  image_file: string;
  image_flag: number;
  image_file_name: string;
}

interface Category {
  id: number;
  category_name: string;
  billing_category_name: string;
  category_code: string;
  hsn_number_id: number;
  order_by: number;
  is_main: number;
  parent_category_id: number | null;
  is_category_pcs: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  is_collection_category: number;
  is_gold_silver: number;
  category_min_stock_id: number | null;
  category_barcode: string;
  prefix_suffix_type: string;
  wastage: string;
  image: string | null;
}

interface Material {
  id: number;
  material_name: string;
}

interface StockNameCode {
  id: number;
  stock_code: string;
  weight: string;
}

interface DesignMaterial {
  id: number;
  design_id: number;
  material_master_id: number | null;
  stock_name_code_id: number | null;
  stone_size_id: number | null;
  material_size: string;
  material_pieces: number;
  material_weight: number;
  material_price: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  material?: Material;
  stock_name_code?: StockNameCode;
}

interface DynamicValue {
  id: number;
  value: string;
}

interface DesignDynamic {
  dynamic_master_id: number;
  name: string;
  dynamicValue: DynamicValue[];
}

export interface DesignDetails {
  id: number;
  image_file: string;
  barcode: string | null;
  variant_number: string;
  variant_attribute: string | null;
  category_id: number;
  item_no: string;
  setting_method: string | null;
  remark: string | null;
  collection: string[] | null;
  show_in_app: number;
  manual_show_in_app: number;
  is_new: number;
  image_files: ImageFile[];
  Category: Category;
  CategoryGroup: any | null;
  design_weights: DesignWeight[];
  design_materials: DesignMaterial[];
  DesignDynamic: DesignDynamic[];
  karat: string;
  gross_weight: string;
  net_weight: string;
  size: number;
  CartData?: Array<{ quantity: number; karat: number; [key: string]: any }>;
}

// UI Component functions (layout only; logic unchanged)
const Label = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={`text-sm font-semibold text-gray-700 ${className || ""}`}>
      {children}
    </div>
  );
};

const ChoicePill = ({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border transition-all shadow-sm ${
        active ? "background-secondary text-secondary-contrast border-[var(--secondary-color)]" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
};

const UserCatalogueDetails = (props: Props) => {
  const { design } = useParams();
  const { karats } = useParams();
  const location = useLocation();
  const addedToCart = useAppSelector((state) => state.leadUserCart.row);
  const cartSummary = useAppSelector((state) => state.leadUserCart.summary);
  const cartQuantity = useAppSelector((state) => state.leadUserCart.addToCart);

  const Details = useAppSelector((state: any) => (state.leadUserCatalogue as any).designDetails) as DesignDetails;
  const dispatch = useAppDispatch();
  const [state, setState] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [activeKarat, setActiveKarat] = useState<number | null>(null);
  const [quanity, setquantity] = useState<any>("0");
  const lastCommittedQuantityRef = useRef<number>(0);
  const quantityStateRef = useRef<string>("0");
  const [karatLoading, setKeratLoading] = useState(false);
  const [selectedDynamicValues, setSelectedDynamicValues] = useState<{ [key: string]: number }>({});
  const [remark, setRemark] = useState<string>("");
  const [activeImage, setActiveImage] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const [cameFromCart, setCameFromCart] = useState(() => location.state?.fromCart === true);
  const cartValuesSetRef = useRef<boolean>(false);

  // useEffect(() => {
  //   if (Number(cartQuantity) > 0) {
  //     setquantity(cartQuantity.toString());
  //   }
  // }, [cartQuantity]);

  // Loading states
  const { isLoading: initialLoading, withLoading, stopLoading } = useLoading(false);

  // Check if user came from cart page and update state
  useEffect(() => {
    const fromCart = location.state?.fromCart === true;
    setCameFromCart(fromCart);
    // Reset cart values ref when location changes (new navigation)
    if (!fromCart) {
      cartValuesSetRef.current = false;
    }
  }, [location.state]);

  // Function to build dynamic_attributes object from selected values
  const buildDynamicAttributes = () => {
    const dynamicAttributes: { [key: string]: number } = {};

    if (Details.DesignDynamic && Details.DesignDynamic.length > 0 && Object.keys(selectedDynamicValues).length > 0) {
      Details.DesignDynamic.forEach((dynamic) => {
        const selectedValueId = selectedDynamicValues[dynamic.name];
        if (selectedValueId) {
          const selectedValue = dynamic.dynamicValue.find((val) => val.id === selectedValueId);
          if (selectedValue) {
            dynamicAttributes[dynamic.name] = selectedValue.id;
          }
        }
      });
    } else {
      Details.DesignDynamic?.forEach((dynamic) => {
        dynamicAttributes[dynamic.name] = dynamic.dynamicValue?.[0]?.id;
      });
    }

    return dynamicAttributes;
  };

  // Helper function to extract and set cart data for current karat
  const prefillCartData = (karat: number) => {
    if (!Details?.id) {
      lastCommittedQuantityRef.current = 0;
      return;
    }

    if (!cameFromCart || !addedToCart?.length) {
      lastCommittedQuantityRef.current = 0;
      return;
    }

    let karatQuantity = 0;
    let dynamicAttributesFromCart: { [key: string]: number } = {};
    let remarkFromCart = "";

    const designCartItem = addedToCart.find((cartGroup: any) => cartGroup.list && cartGroup.list.some((item: any) => item.design_id === Details.id && item.karat === karat));

    if (designCartItem) {
      const karatItem = designCartItem.list.find((item: any) => item.design_id === Details.id && item.karat === karat);

      if (karatItem) {
        karatQuantity = karatItem.quantity;
        remarkFromCart = karatItem.remark || "";

        // Parse DynamicAttributes from cart
        const cartItemWithAttrs = karatItem as any;
        if (cartItemWithAttrs.DynamicAttributes && Array.isArray(cartItemWithAttrs.DynamicAttributes)) {
          cartItemWithAttrs.DynamicAttributes.forEach((attr: any) => {
            const masterName = attr.MasterType?.name;
            const valueId = attr.dynamic_master_value_id;
            if (masterName && valueId) {
              dynamicAttributesFromCart[masterName] = valueId;
            }
          });
        }
      }
    }

    setquantity(String(karatQuantity));

    if (location.state?.fromCart) {
      if (location.state.isDynamicAttributesEmpty) {
        setSelectedDynamicValues({});
        setRemark("");
      } else if (location.state.dynamicAttributes) {
        const foundItem = designCartItem?.list?.find((item: any) => item.id === location.state.dynamicAttributes);
        const attribute = foundItem?.DynamicAttributes;
        if (attribute && Array.isArray(attribute) && attribute[0]?.MasterType?.name) {
          setSelectedDynamicValues(
            attribute.reduce((acc: { [key: string]: number }, curr: any) => {
              acc[curr.MasterType?.name] = curr.MasterValue?.id;
              return acc;
            }, {}) as { [key: string]: number },
          );
        } else {
          setSelectedDynamicValues({});
        }
        setRemark(remarkFromCart);
      } else {
        setSelectedDynamicValues(dynamicAttributesFromCart);
        setRemark(remarkFromCart);
      }
    } else {
      setSelectedDynamicValues(buildDynamicAttributes());
      setRemark(remarkFromCart);
    }
    lastCommittedQuantityRef.current = karatQuantity;
  };

  useEffect(() => {
    if (Details.DesignDynamic && Details.DesignDynamic.length > 0) {
      const allDynamicValuesComplete = Details.DesignDynamic.every((dynamic) => dynamic.dynamicValue && Array.isArray(dynamic.dynamicValue) && dynamic.dynamicValue.length > 0);

      if (allDynamicValuesComplete) {
        setSelectedDynamicValues((prev) => {
          // Check if we're explicitly coming from cart (check location.state directly for accuracy)
          const isFromCart = location.state?.fromCart === true;

          // If coming from cart and values have been set, don't overwrite them
          if (isFromCart && cartValuesSetRef.current) {
            return prev;
          }

          // If coming from cart but values aren't set yet, wait for cart data
          if (isFromCart) {
            // Only preserve existing values if they're already set and valid
            if (Object.keys(prev).length > 0) {
              // Verify that all current dynamic fields have valid values that match the current DesignDynamic
              const allFieldsHaveValidValues = Details.DesignDynamic.every((dynamic) => {
                const existingValue = prev[dynamic.name];
                return existingValue !== undefined && dynamic.dynamicValue.some((val) => val.id === existingValue);
              });
              // Also check that we have values for all fields (not missing any)
              const hasAllFields = Details.DesignDynamic.every((dynamic) => prev[dynamic.name] !== undefined);
              if (allFieldsHaveValidValues && hasAllFields) {
                return prev;
              }
            }
            // If coming from cart but values aren't set yet, return empty to wait for cart data
            return prev;
          }

          // Initialize all dynamic fields with their first option
          // This ensures every field has a default selection on page load/refresh (when not from cart)
          let dynamicAttributes: { [key: string]: number } = {};
          Details.DesignDynamic?.forEach((dynamic) => {
            if (dynamic.dynamicValue && dynamic.dynamicValue.length > 0) {
              // Always set to first value as default for each dynamic field
              dynamicAttributes[dynamic.name] = dynamic.dynamicValue[0]?.id;
            }
          });

          return dynamicAttributes;
        });
      }
    }
  }, [Details, cameFromCart, location.state?.fromCart]);

  const getQuantityBySelectedAttributes = (cartQuantity: LeadUserCartItem[], selectedDynamicValues: { [key: string]: number }, karat?: number) => {
    if (!cartQuantity || cartQuantity.length === 0) {
      return "0";
    }

    const allItems = cartQuantity.flatMap((cat) => cat.list);

    if (!allItems || allItems.length === 0) {
      return "0";
    }

    const isFromCart = location.state?.fromCart === true;
    const isDynamicAttributesEmpty = location.state?.isDynamicAttributesEmpty === true;
    const cartItemId = location.state?.dynamicAttributes;

    // If coming from cart with empty DynamicAttributes, match by cart item ID
    if (isFromCart && isDynamicAttributesEmpty && cartItemId) {
      const matchedItem = allItems.find((item) => {
        return Number(item.id) === Number(cartItemId) && item.CartDesignInfo?.variant_number === Details.variant_number;
      });
      return matchedItem?.quantity.toString() ?? "0";
    }

    // Otherwise, match by dynamic attributes
    const matchedItem = allItems.find((item) => {
      if (item.CartDesignInfo?.variant_number === Details.variant_number) {
        if (karat != null && item.karat !== karat) {
          return false;
        }

        if (!item.DynamicAttributes || item.DynamicAttributes.length === 0) {
          return false;
        }

        return item.DynamicAttributes.every((attr) => {
          const typeName = String(attr.MasterType?.name);
          const selectedId = selectedDynamicValues[typeName];

          if (selectedId == null) return true;

          return attr.MasterValue?.id === selectedId;
        });
      } else {
        return false;
      }
    });

    return matchedItem?.quantity.toString() ?? "0";
  };

  const getRemarkBySelectedAttributes = (cartQuantity: LeadUserCartItem[], selectedDynamicValues: { [key: string]: number }, karat?: number) => {
    if (!cartQuantity || cartQuantity.length === 0) {
      return "";
    }

    const allItems = cartQuantity.flatMap((cat) => cat.list);

    if (!allItems || allItems.length === 0) {
      return "";
    }

    const isFromCart = location.state?.fromCart === true;
    const isDynamicAttributesEmpty = location.state?.isDynamicAttributesEmpty === true;
    const cartItemId = location.state?.dynamicAttributes;

    // If coming from cart with empty DynamicAttributes, match by cart item ID
    if (isFromCart && isDynamicAttributesEmpty && cartItemId) {
      const matchedItem = allItems.find((item) => {
        return Number(item.id) === Number(cartItemId) && item.CartDesignInfo?.variant_number === Details.variant_number;
      });
      return matchedItem?.remark ?? "";
    }

    // Otherwise, match by dynamic attributes
    const matchedItem = allItems.find((item) => {
      if (item.CartDesignInfo?.variant_number === Details.variant_number) {
        if (karat != null && item.karat !== karat) {
          return false;
        }

        if (!item.DynamicAttributes || item.DynamicAttributes.length === 0) {
          return false;
        }

        return item.DynamicAttributes.every((attr) => {
          const typeName = String(attr.MasterType?.name);
          const selectedId = selectedDynamicValues[typeName];

          if (selectedId == null) return true;

          return attr.MasterValue?.id === selectedId;
        });
      } else {
        return false;
      }
    });

    return matchedItem?.remark ?? "";
  };

  quantityStateRef.current = quanity;

  useEffect(() => {
    const fromCart = getQuantityBySelectedAttributes(cartQuantity, selectedDynamicValues, Number(activeKarat));
    const fromListing = location.state?.quantityFromListing;
    const currentQ = quantityStateRef.current;
    const q =
      fromCart !== "0" && fromCart !== undefined
        ? fromCart
        : Number(currentQ) > 0
          ? currentQ
          : (fromListing ?? "0");
    setquantity(q);
    setRemark(getRemarkBySelectedAttributes(cartQuantity, selectedDynamicValues, Number(activeKarat)));
  }, [cartQuantity, selectedDynamicValues, activeKarat, location.state?.quantityFromListing]);

  // Function to handle dynamic value selection
  const handleDynamicValueSelection = (masterName: string, valueId: number) => {
    setSelectedDynamicValues((prev) => ({
      ...prev,
      [masterName]: valueId,
    }));
  };

  const buildDynamicAttributesPayload = () => {
    const dynamicAttributes: { [key: string]: string } = {};

    if (Details.DesignDynamic && Details.DesignDynamic.length > 0) {
      Details.DesignDynamic.forEach((dynamic) => {
        const selectedValueId = selectedDynamicValues[dynamic.name];
        if (selectedValueId) {
          const selectedValue = dynamic.dynamicValue.find((val) => val.id === selectedValueId);
          if (selectedValue) {
            dynamicAttributes[dynamic.name] = selectedValue.value;
          }
        }
      });
    }

    return dynamicAttributes;
  };

  const { branding } = useTheme();
  const placeholderImageUrl =
    branding?.logo_url ||
    "https://jm-manufacturer.s3.ap-south-1.amazonaws.com/jm-manufacturer/siteSetting/image/JewelMaker-Placeholder.jpg";

  const galleryItems: ReactImageGalleryItem[] =
    Details.image_files && Details.image_files.length > 0
      ? Array.from(
          new Map(
            Details.image_files.map((img) => [
              img.image_file,
              {
                original:
                  img.image_file === "https://uatcdn.jewelmaker.ai/vkjdev/design/image/jmlogo.png"
                    ? placeholderImageUrl
                    : img.image_file,
                thumbnail:
                  img.image_file === "https://uatcdn.jewelmaker.ai/vkjdev/design/image/jmlogo.png"
                    ? placeholderImageUrl
                    : img.image_file,
              },
            ]),
          ).values(),
        )
      : Details.image_file
        ? [
            {
              original:
                Details.image_file === "https://uatcdn.jewelmaker.ai/vkjdev/design/image/jmlogo.png"
                  ? placeholderImageUrl
                  : Details.image_file,
              thumbnail:
                Details.image_file === "https://uatcdn.jewelmaker.ai/vkjdev/design/image/jmlogo.png"
                  ? placeholderImageUrl
                  : Details.image_file,
            },
          ]
        : [
            {
              original: placeholderImageUrl,
              thumbnail: placeholderImageUrl,
            },
          ];

  const RemoveFromtheCart = async () => {
    if (isLoading) return;

    if (Number(quanity) > 0) {
      setIsLoading(true);
      try {
        const newQuantity = Number(quanity) - 1;
        setquantity(String(newQuantity));
        const res = await dispatch(
          addToCart({
            add_cart: [
              {
                design_id: Details.id,
                category_id: Details.category_id,
                karat: activeKarat || Details.design_weights[0]?.karat || 0,
                quantity: newQuantity,
                remark: remark,
                dynamic_attributes: buildDynamicAttributes(),
              },
            ],
          }),
        );

        if (res.payload.success) {
          await dispatch(getAllCart());
          if (Number(quanity) - 1 === 0) {
            setState(true);
          }
        }
      } catch (error) {
        toastComponent("Failed to remove item from cart", "error");
        setquantity((prev: any) => String(Number(prev) + 1));
      } finally {
        setIsLoading(false);
      }
    } else {
      setState(true);
    }
  };

  const handleIncrementQuantity = async () => {
    if (isLoading) return;
    const newQuantity = Number(quanity) + 1;
    setquantity(String(newQuantity));
    setIsLoading(true);
    try {
      const res = await dispatch(
        addToCart({
          add_cart: [
            {
              design_id: Details.id,
              category_id: Details.category_id,
              karat: activeKarat || Details.design_weights[0]?.karat || 0,
              quantity: newQuantity,
              remark: remark,
              dynamic_attributes: buildDynamicAttributesPayload(),
            },
          ],
        }),
      );
      if (res.payload?.success) {
        await dispatch(getAllCart());
        lastCommittedQuantityRef.current = newQuantity;
        setState(false);
      }
    } catch (error) {
      toastComponent("Failed to update quantity", "error");
      setquantity(String(newQuantity - 1));
    } finally {
      setIsLoading(false);
    }
  };

  // Function to reset form to initial state
  const resetForm = () => {
    setquantity(cartQuantity.toString());
    setRemark("");
    setSelectedDynamicValues({});
    lastCommittedQuantityRef.current = 0;
    setState(true);
  };

  const AddToCart = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const targetQuantity = Number(quanity) === 0 ? 1 : Number(quanity);
      setquantity(String(targetQuantity));
      const res = await dispatch(
        addToCart({
          add_cart: [
            {
              design_id: Details.id,
              category_id: Details.category_id,
              karat: activeKarat || Details.design_weights[0]?.karat || 0,
              quantity: targetQuantity,
              remark: remark,
              dynamic_attributes: buildDynamicAttributesPayload(),
            },
          ],
        }),
      );

      if (res.payload.success) {
        await dispatch(getAllCart());
        toastComponent("Added to cart", "success");
        lastCommittedQuantityRef.current = targetQuantity;
        setState(false);
        // Reset form after successful addition
        // resetForm();
      }
    } catch (error) {
      toastComponent("Failed to add item to cart", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (karats) {
      handleKaratSelect(Number(karats));
    } else {
      const firstKarat = Details.design_weights?.[0]?.karat;
      if (firstKarat) {
        handleKaratSelect(firstKarat);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Details]);

  // Check if Details already exists to stop loading
  useEffect(() => {
    if (Details && Details.id) {
      stopLoading();
    }
  }, [Details, stopLoading]);

  useEffect(() => {
    dispatch(getAllCart());
  }, [design, dispatch]);

  // Handle setting values from cart immediately when coming from cart
  useEffect(() => {
    const isFromCart = location.state?.fromCart === true;

    // Only set cart values once and immediately when all data is ready
    if (isFromCart && !cartValuesSetRef.current && Details?.id && Details.DesignDynamic && Details.DesignDynamic.length > 0 && addedToCart?.length > 0) {
      const allDynamicValuesComplete = Details.DesignDynamic.every((dynamic) => dynamic.dynamicValue && Array.isArray(dynamic.dynamicValue) && dynamic.dynamicValue.length > 0);

      if (allDynamicValuesComplete) {
        // Find the cart item based on location.state.dynamicAttributes (cart item id)
        const designCartItem = addedToCart.find((cartGroup: any) => cartGroup.list && cartGroup.list.some((item: any) => item.design_id === Details.id));

        if (location.state.dynamicAttributes) {
          const foundItem = designCartItem?.list?.find((item: any) => Number(item.id) === Number(location.state.dynamicAttributes));

          if (foundItem) {
            const attribute = foundItem.DynamicAttributes;

            if (location.state.isDynamicAttributesEmpty && (!attribute || attribute.length === 0)) {
              // If dynamic attributes are empty, set empty values
              setSelectedDynamicValues({});
              cartValuesSetRef.current = true;
            } else if (attribute && Array.isArray(attribute) && attribute.length > 0) {
              // Set values from cart item's DynamicAttributes
              const cartValues = attribute.reduce((acc: { [key: string]: number }, curr: any) => {
                if (curr.MasterType?.name && curr.MasterValue?.id) {
                  acc[curr.MasterType.name] = curr.MasterValue.id;
                }
                return acc;
              }, {});

              // Ensure all dynamic fields have values (fill missing ones with first option)
              const completeValues: { [key: string]: number } = {};
              Details.DesignDynamic.forEach((dynamic) => {
                if (cartValues[dynamic.name]) {
                  // Use cart value if it exists and is valid
                  const isValid = dynamic.dynamicValue.some((val) => val.id === cartValues[dynamic.name]);
                  completeValues[dynamic.name] = isValid ? cartValues[dynamic.name] : dynamic.dynamicValue[0]?.id;
                } else {
                  // Use first option if cart value doesn't exist
                  completeValues[dynamic.name] = dynamic.dynamicValue[0]?.id;
                }
              });

              setSelectedDynamicValues(completeValues);
              cartValuesSetRef.current = true;
            }
          }
        } else if (designCartItem) {
          // If no specific dynamicAttributes id, use the first matching item
          const karatItem = designCartItem.list.find((item: any) => item.design_id === Details.id);
          if (karatItem && karatItem.DynamicAttributes && Array.isArray(karatItem.DynamicAttributes) && karatItem.DynamicAttributes.length > 0) {
            const cartValues = karatItem.DynamicAttributes.reduce((acc: { [key: string]: number }, curr: any) => {
              if (curr.MasterType?.name && curr.dynamic_master_value_id) {
                acc[curr.MasterType.name] = curr.dynamic_master_value_id;
              }
              return acc;
            }, {});

            // Ensure all dynamic fields have values
            const completeValues: { [key: string]: number } = {};
            Details.DesignDynamic.forEach((dynamic) => {
              if (cartValues[dynamic.name]) {
                const isValid = dynamic.dynamicValue.some((val) => val.id === cartValues[dynamic.name]);
                completeValues[dynamic.name] = isValid ? cartValues[dynamic.name] : dynamic.dynamicValue[0]?.id;
              } else {
                completeValues[dynamic.name] = dynamic.dynamicValue[0]?.id;
              }
            });

            setSelectedDynamicValues(completeValues);
            cartValuesSetRef.current = true;
          }
        }
      }
    }
  }, [Details, addedToCart, location.state?.fromCart, location.state?.dynamicAttributes, location.state?.isDynamicAttributesEmpty]);

  useEffect(() => {
    if (Details?.Category?.category_name !== undefined && Details?.Category?.category_name.length > 0) {
      document.title = `JewelMaker | ${Details?.Category?.category_name}`;
    } else {
      document.title = `JewelMaker`;
    }
  }, [Details?.Category?.category_name]);

  useEffect(() => {
    const designCartItem = addedToCart.find((cartGroup: any) => cartGroup.list && cartGroup.list.some((item: any) => item.design_id === Details.id && item.karat === activeKarat));
    const loadDesignDetails = async () => {
      if (karats) {
        setKeratLoading(true);
        const data = await withLoading(() => dispatch(designDetailsForLead({ design: design, karat: karats })));
        const payload = data?.payload;
        const designData = payload?.design;
        const designDynamic = designData?.DesignDynamic;
        const hasDesignDynamic = Array.isArray(designDynamic) && designDynamic.length > 0;

        // Don't overwrite cart values if they've already been set
        const isFromCart = location.state?.fromCart === true;
        if (isFromCart && cartValuesSetRef.current) {
          // Cart values already set, just select the karat
          handleKaratSelect(Number(karats));
          setKeratLoading(false);
          return;
        }

        if (location.state && location.state.dynamicAttributes) {
          const dynamicAttributes = designCartItem?.list.find((i: any) => Number(i.id) === Number(location.state.dynamicAttributes))?.DynamicAttributes;
          if (location.state?.fromCart && location.state.dynamicAttributes && dynamicAttributes && dynamicAttributes.length === 0) {
            setSelectedDynamicValues({});
            cartValuesSetRef.current = true;
          } else if (dynamicAttributes && dynamicAttributes.length > 0) {
            setSelectedDynamicValues(
              dynamicAttributes.reduce((acc: { [key: string]: number }, curr: any) => {
                acc[curr.MasterType.name] = curr.MasterValue.id;
                return acc;
              }, {}) as { [key: string]: number },
            );
            cartValuesSetRef.current = true;
          } else if (payload?.success && hasDesignDynamic) {
            // Only set default if not from cart
            const first = designDynamic[0];
            const firstValue = first?.dynamicValue?.[0];
            if (!isFromCart && first?.name && firstValue?.id != null) {
              setSelectedDynamicValues({ [first.name]: firstValue.id });
            }
          }
        } else if (payload?.success && hasDesignDynamic) {
          // Only set default if not from cart (build from API response; Details may not be updated yet)
          if (!isFromCart) {
            const dynamicAttributes: { [key: string]: number } = {};
            designDynamic.forEach((dynamic: any) => {
              const firstId = dynamic.dynamicValue?.[0]?.id;
              if (dynamic.name != null && firstId != null) dynamicAttributes[dynamic.name] = firstId;
            });
            setSelectedDynamicValues(dynamicAttributes);
          }
        }
        // Remove setTimeout delay - set karat immediately
        handleKaratSelect(Number(karats));
        setKeratLoading(false);
      }
    };
    loadDesignDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design, karats]);

  const { id } = useParams();

  function handleRemoveall() {
    setDeleteModal(false);
    const idArray: any = [];
    addedToCart.forEach((rowItem) => {
      rowItem.list.forEach((listItem) => {
        idArray.push(listItem.id);
      });
    });
    dispatch(removeCartData({ cart_id: idArray })).then((res) => {
      toastComponent(res.payload.message, "success");
      dispatch(getAllCart());
    });
    setDeleteModal(false);
  }

  const handleKaratSelect = (karat: number) => {
    setKeratLoading(true);
    setActiveKarat(karat);

    // Use the centralized prefill function
    prefillCartData(karat);

    setKeratLoading(false);
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^0-9]/g, "");
    setquantity(sanitized === "" ? "0" : sanitized);
  };

  const commitQuantityChange = async (targetQuantity: number) => {
    if (isLoading) return;
    if (Number.isNaN(targetQuantity) || targetQuantity < 0) targetQuantity = 0;

    const previousQuantity = lastCommittedQuantityRef.current;
    const delta = targetQuantity - previousQuantity;
    if (delta === 0) return;

    setIsLoading(true);
    try {
      const res = await dispatch(
        addToCart({
          add_cart: [
            {
              design_id: Details.id,
              category_id: Details.category_id,
              karat: activeKarat || Details.design_weights[0]?.karat || 0,
              quantity: targetQuantity,
              remark: remark,
              dynamic_attributes: buildDynamicAttributesPayload(),
            },
          ],
        }),
      );

      if (res.payload.success) {
        await dispatch(getAllCart());
        lastCommittedQuantityRef.current = targetQuantity;
        if (targetQuantity === 0) setState(true);
        else setState(false);
      }
    } catch (error) {
      toastComponent("Failed to update quantity", "error");
      setquantity(String(previousQuantity));
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityInputBlur = async () => {
    await commitQuantityChange(Number(quanity));
  };

  // Get the first design weight for display
  const primaryWeight = Details.design_weights?.[0];

  // Get the selected design weight for display
  const selectedWeight = activeKarat ? Details.design_weights?.find((weight) => weight.karat === activeKarat) : primaryWeight;

  const gross = selectedWeight?.gross_weight != null ? Number(selectedWeight.gross_weight).toFixed(3) : "0.000";
  const net = selectedWeight?.net_weight != null ? Number(selectedWeight.net_weight).toFixed(3) : "0.000";
  const karatDisplay = selectedWeight?.karat != null ? String(selectedWeight.karat) : "0";
  const categoryName = Details?.Category?.category_name ?? "--";
  const variantNumber = Details?.variant_number ?? "Product";

  return (
    <div className="min-h-screen bg-white">
      {initialLoading && <AppLoader message="Loading product details..." />}
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-sm text-gray-500 flex items-center gap-2">
        <button onClick={() => navigate(`/catalogues/${id}`)} className="hover:underline hover:text-[#396897] transition-colors">
          Catalog
        </button>
        <ChevronRight className="w-4 h-4" />
        <span className="truncate">{Details?.Category?.category_name?.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase()) || "Product"}</span>
      </div>

      {/* Header with Cart */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end items-start">
          <CartButton />
        </div>
      </div>

      <div className="w-full bg-white py-8 md:py-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            {/* Left Section - Image (ProductDetails-style) */}
            <div className="relative bg-white rounded-xl md:rounded-2xl w-full">
              {/* Logo overlay */}
              {/* <div className="absolute top-6 left-6 z-10 flex items-center gap-2">
                <img src={JewelMakerLogo} alt="JewelMaker Logo" className="md:h-6" />
              </div> */}

              <ImageHoverZoom src={galleryItems[activeImage]?.original || galleryItems[0]?.original} alt={Details?.Category?.category_name ?? variantNumber} />

              {/* Weight info overlay — position relative to parent */}
              {/* <div className="absolute bottom-4 right-4 px-4 py-3 space-y-1 text-center  rounded-lg z-10">
                <p className="text-sm md:text-base font-normal text-gray-800">
                  {variantNumber}
                </p>
                <p className="text-xs md:text-sm text-gray-600">
                  Gross Wt: {gross}
                </p>
                <p className="text-xs md:text-sm text-gray-600">
                  Net Wt: {net}
                </p>
              </div> */}
            </div>

            {/* Right Section - Product Information (ProductDetails-style) */}
            <div className="flex flex-col space-y-6 md:space-y-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-normal text-[#0E0E0E]">
                  {variantNumber}
                </h1>
              </div>

              {/* Remark Section */}
              <div>
                <label className="block text-sm md:text-base font-normal text-gray-700 mb-2">
                  REMARK:
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Enter additional notes or instructions here..."
                  className="w-full px-4 py-3 bg-[#F3F3F3] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)] focus:border-transparent resize-none"  
                  rows={4}
                />
              </div>

              {/* Product Details */}
              <div>
                <h2 className="text-lg md:text-xl font-normal text-[#0E0E0E] mb-4">
                  PRODUCT DETAILS:
                </h2>
                <div className="space-y-1">
                  <div className="flex justify-between py-0.5">
                    <span className="text-sm md:text-base">
                      Item Code:
                    </span>
                    <span className="text-sm md:text-base font-normal">
                      {variantNumber}
                    </span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-sm md:text-base">
                      Category:
                    </span>
                    <span className="text-sm md:text-base font-normal">
                      {categoryName}
                    </span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-sm md:text-base">
                      Gross Wt:
                    </span>
                    <span className="text-sm md:text-base font-normal">
                      {gross}
                    </span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-sm md:text-base">
                      Net Wt:
                    </span>
                    <span className="text-sm md:text-base font-normal">
                      {net}
                    </span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-sm md:text-base">
                      Metal Karat:
                    </span>
                    <span className="text-sm md:text-base font-normal">
                      {karatDisplay}
                    </span>
                  </div>
                </div>
              </div>

              {/* Karat Selection */}
              {Details.design_weights && Details.design_weights.length > 0 && (
                <div>
                  <Label>Choose Karat</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Details.design_weights.map((weight) => (
                      <ChoicePill key={weight.karat} active={activeKarat === weight.karat} onClick={() => handleKaratSelect(weight.karat)}>
                        {weight.karat}K
                      </ChoicePill>
                    ))}
                  </div>
                </div>
              )}

              {/* Dynamic Master Details */}
              {Details.DesignDynamic && Details.DesignDynamic.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4">
                  {Details.DesignDynamic.map((dynamic) => (
                    <div key={dynamic.dynamic_master_id}>
                      <Label className="capitalize">{dynamic.name}</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {dynamic.dynamicValue.map((value) => {
                          const isSelected = selectedDynamicValues[dynamic.name] === value.id;
                          return (
                            <ChoicePill key={value.id} active={isSelected} onClick={() => handleDynamicValueSelection(dynamic.name, value.id)}>
                              {value.value}
                            </ChoicePill>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity Selector (PCS) - ProductDetails-style pill */}
              {activeKarat && !karatLoading && (
                <div className="flex items-center gap-6 pt-2">
                  <span className="text-xl md:text-2xl font-normal text-[#0E0E0E]">
                    PCS:
                  </span>
                  <div className="relative h-10 md:h-12 w-28 md:w-36">
                    <div className="absolute inset-0 rounded-md bg-[#F3F3F3] border border-[#BFC6E4]" />
                    <button
                      type="button"
                      onClick={() => RemoveFromtheCart()}
                      disabled={Number(quanity) === 0 || isLoading}
                      className={`absolute cursor-pointer left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center shadow-sm ${
                        Number(quanity) === 0 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "background-secondary text-secondary-contrast"
                      }`}
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={3} />
                    </button>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <input
                        type="number"
                        value={quanity}
                        onChange={handleQuantityInputChange}
                        onBlur={handleQuantityInputBlur}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        }}
                        disabled={isLoading}
                        className="w-14 bg-transparent text-center border-0 focus:outline-none focus:ring-0 text-xl md:text-2xl font-normal text-[#0E0E0E]"
                        min={0}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleIncrementQuantity}
                      disabled={isLoading}
                      className="absolute cursor-pointer right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 md:w-8 md:h-8 background-secondary text-secondary-contrast rounded-full flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={3} />
                    </button>
                  </div>
                </div>
              )}

              {karatLoading && (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-[var(--secondary-color)] border-t-transparent rounded-full animate-spin shadow-sm" />
                </div>
              )}

              {/* Action Buttons - ProductDetails-style */}
              {/* {activeKarat && !karatLoading && (
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={AddToCart}
                    disabled={isLoading}
                    className="flex-1 min-h-12 px-6 py-3 background-secondary text-secondary-contrast font-normal rounded-lg shadow-sm hover:opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                  >
                    <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    Add to Cart
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/catalogues/${id}/order-cart`)}
                    className="flex-1 min-h-12 px-6 py-3 bg-gray-200 text-gray-800 font-normal rounded-lg border border-gray-300 hover:bg-gray-300 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  >
                    View Cart
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                  </button>
                </div>
              )} */}
            </div>
          </div>

          {/* Material Details - below grid, full width */}
          {Details.design_materials && Details.design_materials.length > 0 && (
            <div className="mt-12 bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg md:text-xl font-normal text-[#0E0E0E] mb-4">
                Material Details
              </h3>
              <div className="space-y-3">
                {Details.design_materials.map((material) => (
                  <div key={material.id} className="rounded-xl bg-[#F3F3F3] p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="font-semibold text-gray-900" >
                        {material?.stock_name_code?.stock_code || material?.material?.material_name || "N/A"}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-700 mb-1 text-right">
                          Size
                        </div>
                        <div className="text-base font-medium text-gray-900 text-right">
                          {material.material_size}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-700 mb-1 text-right">
                          Pieces
                        </div>
                        <div className="text-base font-medium text-gray-900 text-right">
                          {material.material_pieces}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-700 mb-1 text-right">
                          Weight
                        </div>
                        <div className="text-base font-medium text-gray-900 text-right">
                          {material.material_weight}g
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Zoom Modal */}
      {showZoom && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4" onClick={() => setShowZoom(false)}>
          <button
            onClick={() => setShowZoom(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={galleryItems[activeImage]?.original || galleryItems[0]?.original}
            alt={Details?.Category?.category_name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default UserCatalogueDetails;
